import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AppShell } from "@/components/AppShell";
import { api } from "@/lib/api";
import { ChevronRight, AlertTriangle, CheckCircle2, Clock, Loader2 } from "lucide-react";

const FILTERS = [
  { id: "all", label: "Tous" },
  { id: "pending", label: "En attente" },
  { id: "resolved", label: "Résolus" },
];

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

export const AdminDefects = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = () => api.get("/admin/defects").then(({ data }) => setItems(data.items || [])).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const filtered = filter === "all" ? items : items.filter((d) => (filter === "resolved" ? d.resolved : !d.resolved));
  const pendingCount = items.filter((d) => !d.resolved).length;

  const resolve = async (id, e) => {
    e?.stopPropagation();
    setBusy(id);
    try {
      await api.post(`/admin/defects/${id}/resolve`);
      setItems((arr) => arr.map((d) => (d.id === id ? { ...d, resolved: true, resolved_at: new Date().toISOString(), seen: true } : d)));
      window.dispatchEvent(new Event("notif-refresh"));
      toast.success("Défaut marqué comme résolu");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally {
      setBusy(null);
    }
  };

  return (
    <AppShell mode="admin">
      <div className="mb-8">
        <div className="font-mono text-xs text-signal-error tracking-[0.3em] uppercase mb-2 flex items-center gap-2">
          <AlertTriangle className="w-3.5 h-3.5" /> // Contrôle Qualité
        </div>
        <h1 className="font-display text-4xl font-bold uppercase tracking-tight">Journal des défauts</h1>
        <p className="font-mono text-[11px] text-zinc-500 mt-2">Problèmes signalés par les clients sur un fichier livré — suivi de résolution.</p>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-6">
        {FILTERS.map((f) => {
          const count = f.id === "all" ? items.length : f.id === "resolved" ? items.length - pendingCount : pendingCount;
          return (
            <button
              key={f.id}
              data-testid={`defect-filter-${f.id}`}
              onClick={() => setFilter(f.id)}
              className={`px-4 py-2 font-mono text-[11px] uppercase tracking-widest rounded-sm border transition-colors ${filter === f.id ? "border-signal-error text-signal-error bg-signal-error/10" : "border-white/10 text-zinc-400 hover:border-white/30"}`}
            >
              {f.label} <span className="text-zinc-600">({count})</span>
            </button>
          );
        })}
        {pendingCount > 0 && (
          <div className="ml-auto font-mono text-[11px] uppercase tracking-widest text-signal-error flex items-center gap-1.5" data-testid="defect-pending-summary">
            <AlertTriangle className="w-3.5 h-3.5" /> {pendingCount} en attente
          </div>
        )}
      </div>

      <div className="glass rounded-md overflow-hidden">
        <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 border-b border-white/10 font-mono text-[10px] uppercase tracking-widest text-zinc-500 items-center">
          <div className="col-span-3">Client</div>
          <div className="col-span-2">Véhicule</div>
          <div className="col-span-3">Problème</div>
          <div className="col-span-2">Signalé le</div>
          <div className="col-span-2 text-right">Statut</div>
        </div>
        {loading ? (
          <div className="font-mono text-xs text-zinc-500 py-12 text-center">Chargement...</div>
        ) : filtered.length === 0 ? (
          <p className="text-zinc-500 text-sm py-16 text-center" data-testid="defect-empty">Aucun défaut dans cette catégorie.</p>
        ) : (
          filtered.map((d) => (
            <div
              key={d.id}
              data-testid={`defect-row-${d.id}`}
              onClick={() => navigate(`/admin/orders/${d.id}`)}
              className={`grid grid-cols-2 md:grid-cols-12 gap-4 px-6 py-4 border-b border-white/5 last:border-0 hover:bg-white/5 transition-colors items-center cursor-pointer ${!d.resolved ? "bg-signal-error/[0.04]" : ""}`}
            >
              <div className="md:col-span-3 min-w-0">
                <div className="text-sm truncate flex items-center gap-2">
                  {d.user_name || "Client"}
                  {!d.seen && !d.resolved && <span data-testid={`defect-new-${d.id}`} title="Non vu" className="w-1.5 h-1.5 rounded-full bg-signal-error animate-pulse shrink-0" />}
                </div>
                <div className="font-mono text-[10px] text-zinc-500 truncate">{d.user_email}</div>
              </div>
              <div className="md:col-span-2 font-display uppercase tracking-tight text-sm hidden md:block truncate">{d.vehicle || "—"}</div>
              <div className="md:col-span-3 text-xs text-zinc-300 leading-snug min-w-0">
                <span className="line-clamp-2">{d.reason || "Signalé par le client"}</span>
                {d.solution_ref && <div className="font-mono text-[9px] text-zinc-600 mt-1 truncate">réf. {d.solution_ref}</div>}
              </div>
              <div className="md:col-span-2 font-mono text-[10px] text-zinc-400 hidden md:block">{fmtDate(d.reported_at)}</div>
              <div className="md:col-span-2 flex justify-end items-center gap-2">
                {d.resolved ? (
                  <span data-testid={`defect-status-${d.id}`} className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest px-2 py-1 rounded-sm bg-signal-success/10 text-signal-success border border-signal-success/40">
                    <CheckCircle2 className="w-3 h-3" /> Résolu
                  </span>
                ) : (
                  <>
                    <span data-testid={`defect-status-${d.id}`} className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest px-2 py-1 rounded-sm bg-signal-error/10 text-signal-error border border-signal-error/40">
                      <Clock className="w-3 h-3" /> En attente
                    </span>
                    <button
                      data-testid={`defect-resolve-${d.id}`}
                      onClick={(e) => resolve(d.id, e)}
                      disabled={busy === d.id}
                      title="Marquer comme résolu"
                      className="w-7 h-7 grid place-items-center rounded-sm border border-white/10 text-zinc-400 hover:border-signal-success hover:text-signal-success transition-colors shrink-0 disabled:opacity-50"
                    >
                      {busy === d.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                    </button>
                  </>
                )}
                <ChevronRight className="w-4 h-4 text-zinc-600 hidden md:block" />
              </div>
            </div>
          ))
        )}
      </div>
    </AppShell>
  );
};
